import type React from "react";

import { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  useColorModeValue,
  Tooltip as ChakraTooltip,
} from "@chakra-ui/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";

interface RankingData {
  name: string;
  value: number;
}

interface RankingChartItemProps {
  title: string;
  entityType: "corban" | "promotora" | "vendedor";
  dataType: "liberado" | "consulta";
  dashboardData: any;
  isLoading?: boolean;
}

const RankingChartItem: React.FC<RankingChartItemProps> = ({
  entityType,
  dataType,
  dashboardData,
  isLoading = false,
}) => {
  const [rankingData, setRankingData] = useState<RankingData[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const barColor = "#008000";

  const formatCurrency = (value: number) => {
    if (dataType === "consulta") {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)} Mi`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)} Mil`;
      }
      return value.toFixed(2);
    } else {
      if (value >= 1000000) {
        return `R$ ${(value / 1000000).toFixed(1)} Mi`;
      } else if (value >= 1000) {
        return `R$ ${(value / 1000).toFixed(1)} Mil`;
      }
      return `R$ ${value.toFixed(2)}`;
    }
  };

  useEffect(() => {
    if (!dashboardData) return;

    setIsProcessing(true);

    try {
      const sourceData =
        dataType === "liberado"
          ? dashboardData.totalSumReleasedAmountGraphic
          : dashboardData.quantityBalanceGraphic;

      if (!sourceData || !Array.isArray(sourceData)) {
        setRankingData([]);
        return;
      }

      const dataMap = new Map<string, number>();

      sourceData.forEach((item) => {
        let key = "";

        if (entityType === "corban" && item.corbanName) {
          key = item.corbanName;
        } else if (entityType === "promotora" && item.promoterName) {
          key = item.promoterName;
        } else if (entityType === "vendedor" && item.sellerFullName) {
          key = item.sellerFullName;
        }

        if (!key) return;

        const currentValue = dataMap.get(key) || 0;
        dataMap.set(key, currentValue + (item.total || 0));
      });

      // Convert map to array and sort
      const dataArray = Array.from(dataMap, ([name, value]) => ({
        name,
        value,
      }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 10); // Top 10

      setRankingData(dataArray);
    } catch (error) {
      console.error(
        `Error processing ${entityType} ${dataType} ranking data:`,
        error
      );
      setRankingData([]);
    } finally {
      setIsProcessing(false);
    }
  }, [dashboardData, entityType, dataType]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    const bgColor = useColorModeValue("white", "gray.800");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const textColor = useColorModeValue("gray.800", "white");

    if (active && payload && payload.length) {
      return (
        <Box
          bg={bgColor}
          p={3}
          borderRadius="md"
          boxShadow="sm"
          border="1px solid"
          borderColor={borderColor}
          maxW="280px"
        >
          <Text fontWeight="bold" mb={2} fontSize="sm" color={textColor}>
            {label}
          </Text>
          <Text color={textColor} fontSize="sm">
            {formatCurrency(payload[0].value)}
          </Text>
        </Box>
      );
    }
    return null;
  };

  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, value } = props;

    const threshold = 65;
    const isInside = width >= threshold;

    if (width < 30) return null;

    return (
      <g>
        <text
          x={isInside ? x + width - 10 : x + width + 5}
          y={y + height / 2}
          fill={isInside ? "#FFFFFF" : "#008000"}
          textAnchor={isInside ? "end" : "start"}
          dominantBaseline="middle"
          fontSize="12px"
          fontWeight="500"
        >
          {formatCurrency(value)}
        </text>
      </g>
    );
  };

  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const fullName = payload.value;
    const displayName =
      fullName.length > 16 ? `${fullName.substring(0, 16)}...` : fullName;

    return (
      <ChakraTooltip
        label={fullName}
        isDisabled={fullName.length <= 16}
        placement="left"
        hasArrow
      >
        <g transform={`translate(${x},${y})`}>
          <text x={-3} y={0} dy={4} textAnchor="end" fill="#666" fontSize={12}>
            {displayName}
          </text>
        </g>
      </ChakraTooltip>
    );
  };

  const getChartHeight = (dataLength: number) => {
    const itemHeight = 40;
    const minHeight = 250;
    const maxHeight = 500;

    const calculatedHeight = dataLength * itemHeight;
    return Math.min(Math.max(calculatedHeight, minHeight), maxHeight);
  };

  if (isProcessing || isLoading) {
    return (
      <Flex justify="center" align="center" h="300px">
        <Spinner size="xl" color="green.500" />
      </Flex>
    );
  }

  return (
    <Box>
      {rankingData.length > 0 ? (
        <Box height={getChartHeight(rankingData.length)} width="100%">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rankingData}
              layout="vertical"
              margin={{ top: 10, right: 60, left: 100, bottom: 10 }}
              barCategoryGap={10}
              barGap={4}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={true}
                vertical={false}
              />
              <XAxis
                type="number"
                tickFormatter={formatCurrency}
                domain={[0, "dataMax"]}
                padding={{ right: 20 }}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={100}
                interval={0}
                tick={<CustomYAxisTick />}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                barSize={24}
                fill={barColor}
              >
                <LabelList dataKey="value" content={renderCustomizedLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Flex
          h="300px"
          alignItems="center"
          justifyContent="center"
          color="gray.500"
        >
          Nenhum dado disponível
        </Flex>
      )}
    </Box>
  );
};

export default RankingChartItem;
